import React, { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  InOutModalTitleState,
  inOutMonthCustomFilterState,
  inOutMonthFilterState,
  inOutResetFieldsState,
  toastBgState,
  toastMessageState,
} from "../../utils/atoms/atoms";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CustomTable from "../common/CustomTable";
import { inoutColumns } from "../../utils/table/inOutCols";
import CustomModal from "../CustomModal";
import InOutFormBody from "./InOutFormBody";
import LoadingComponent from "../../LoadingComponent";
import ToastNotification from "../common/ToastNotification";
import { getStockCurrent, getStockPast, getStockCustom } from "../../api/inout";
import BulkUpdateModalBody from "../common/BulkUpdateModalBody";
import { set, useForm } from "react-hook-form";
import { getMonthDateString, getMonthFullName } from "../../utils/funcs/time";
import { HasRole } from "../../context/HasRole";
import { inoutTanotiColumns } from "../../utils/table/inOutTanotiCols";

interface IMonthMutation {
  monthFilterType: string;
  month: string;
}

const InOutDashboard = () => {
  const [monthFilterType, setMonthFilterType] = useRecoilState(
    inOutMonthFilterState
  );
  const [monthCustomFilter, setMonthCustomFilter] = useRecoilState(
    inOutMonthCustomFilterState
  );
  const { register, handleSubmit, watch, reset } = useForm();

  let stockDataFn = getStockCurrent;
  if (monthFilterType === "past") {
    stockDataFn = getStockPast;
  } else if (monthFilterType === "custom") {
    stockDataFn = () => getStockCustom(monthCustomFilter);
  }

  const {
    isLoading: stockLoading,
    data: stockData,
    isError: stockError,
    isSuccess: stockSuccess,
  } = useQuery({
    queryKey: ["stock-data"],
    queryFn: stockDataFn,
  });

  const stockMutation = useMutation({
    mutationFn: async (monthDetails: IMonthMutation) => {
      if (monthDetails["monthFilterType"] === "current") {
        return getStockCurrent();
      } else if (monthDetails["monthFilterType"] === "past") {
        return getStockPast();
      } else if (
        monthDetails["monthFilterType"] === "custom" &&
        monthDetails["month"] !== ""
      ) {
        return getStockCustom(monthDetails["month"]);
      } else {
        return "";
      }
    },
    onSuccess(data) {
      queryClient.setQueryData(["stock-data"], data);
    },
  });

  const isLoading = stockLoading;
  const isSuccess = stockSuccess;
  const toastBg = useRecoilValue(toastBgState);
  const toastMessage = useRecoilValue(toastMessageState);
  const setInOutResetFields = useSetRecoilState(inOutResetFieldsState);
  const queryClient = useQueryClient();

  const resetInOutFormMutation = () => {
    queryClient.removeQueries({ queryKey: ["in-out-item"] });
  };
  const isError = stockError;
  const [inOutModalTitle, setInOutModalTitle] =
    useRecoilState(InOutModalTitleState);

  useEffect(() => {
    // TypeScript users
    const subscription = watch(() =>
      handleSubmit((data, event) => {
        event?.preventDefault();
        if (data["inout_month"] !== undefined) {
          setMonthFilterType("custom");
          stockMutation.mutate({
            monthFilterType: "custom",
            month: data["inout_month"],
          });
          setMonthCustomFilter(data["inout_month"]);
        }
      })()
    );

    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  return (
    <LoadingComponent
      isError={isError}
      isLoading={isLoading}
      isSuccess={isSuccess}
      ComponentBody={
        <React.Fragment>
          <div className="inventory-header d-flex justify-content-between mt-3 mb-3">
            <HasRole roles={["admin", "accounts", "production"]}>
              <div className="d-flex gap-2">
                <HasRole roles={["admin", "accounts"]}>
                  <div
                    className="styled-btn--std mobile-d-none"
                    data-bs-toggle="modal"
                    data-bs-target="#bulkInsertInOutModal"
                    onClick={() => {
                      setInOutModalTitle("Bulk Update Inventory");
                    }}
                  >
                    Bulk Insert
                  </div>
                </HasRole>
                <div
                  className="styled-btn--std mobile-d-none"
                  data-bs-toggle="modal"
                  data-bs-target="#inOutModal"
                  onClick={() => {
                    resetInOutFormMutation();
                    setInOutResetFields(true);
                    setInOutModalTitle("Add Entry");
                  }}
                >
                  Add +
                </div>
              </div>
            </HasRole>

            <div className="d-flex gap-2">
              <div
                className={`styled-btn${
                  monthFilterType === "current" || monthFilterType === ""
                    ? "--dark"
                    : "--std"
                }`}
                onClick={() => {
                  setMonthFilterType("current");
                  stockMutation.mutate({
                    monthFilterType: "current",
                    month: "",
                  });
                  reset();
                }}
              >
                {getMonthFullName()}
              </div>
              <div
                className={`styled-btn${
                  monthFilterType === "past" ? "--dark" : "--std"
                }`}
                onClick={() => {
                  setMonthFilterType("past");
                  stockMutation.mutate({
                    monthFilterType: "past",
                    month: "",
                  });
                  reset();
                }}
              >
                {getMonthFullName(1)}
              </div>
              <form>
                <input
                  type="month"
                  id=""
                  {...register("inout_month")}
                  max={getMonthDateString()}
                />
              </form>
            </div>
          </div>

          <CustomTable
            data={stockData}
            columns={
              process.env.REACT_APP_COMPANY_NAME === "TANOTI"
                ? inoutTanotiColumns
                : inoutColumns
            }
            isFilter={true}
            isPaginate={true}
            headerBgColor="gray"
          />

          <CustomModal
            modalId="inOutModal"
            modalTitle={inOutModalTitle}
            modalBody={<InOutFormBody />}
          />

          <CustomModal
            modalTitle={inOutModalTitle}
            modalBody={
              <BulkUpdateModalBody
                modalTitle="Bulk Update In Out"
                downloadEndpoint="/in-out/bulk/template/"
                uploadEndpoint="/in-out/bulk"
                successMessage="Entry updated successfully"
                fileName="inout_update_template.xlsx"
                guidelines={[
                  "🔄 For Out entries (status=1): party_id is required.",
                  "👥 Party ID of the respective part should be entered",
                ]}
              />
            }
            modalId="bulkInsertInOutModal"
          />
          <ToastNotification bg={toastBg} message={toastMessage} />
        </React.Fragment>
      }
    />
  );
};

export default InOutDashboard;
